import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { ensureEventId } from '@/hooks/useEvent';
import { Tables } from '@/integrations/supabase/types';
import { toast } from 'sonner';

export type Payment = Tables<'payments'>;
export type PaymentStatus = 'pending' | 'approved' | 'rejected' | 'refunded';

export interface PaymentBankInfo {
  holder: string;
  account: string;
  bank: string;
  ci: string;
  phone: string;
  qr_url: string;
}

export interface PaymentSettings {
  publishPriceUsd: number;
  usdToBsRate: number;
  bankInfo: PaymentBankInfo;
}

const SETTINGS_KEYS = [
  'payment.publish_price_usd',
  'payment.usd_to_bs_rate',
  'payment.bank_info',
] as const;

const DEFAULT_BANK_INFO: PaymentBankInfo = {
  holder: '',
  account: '',
  bank: '',
  ci: '',
  phone: '',
  qr_url: '',
};

// Public payment settings (price, exchange rate, bank info) from app_settings.
// Readable by any authenticated user; only admins can write (RLS enforced).
export function usePaymentSettings() {
  return useQuery({
    queryKey: ['payment-settings'],
    queryFn: async (): Promise<PaymentSettings> => {
      const { data, error } = await supabase
        .from('app_settings')
        .select('key, value')
        .in('key', SETTINGS_KEYS as unknown as string[]);

      if (error) throw error;

      const map = new Map((data || []).map((row) => [row.key, row.value]));

      return {
        publishPriceUsd: Number(map.get('payment.publish_price_usd') ?? 0),
        usdToBsRate: Number(map.get('payment.usd_to_bs_rate') ?? 0),
        bankInfo: {
          ...DEFAULT_BANK_INFO,
          ...((map.get('payment.bank_info') as Partial<PaymentBankInfo> | undefined) ?? {}),
        },
      };
    },
    staleTime: 5 * 60 * 1000,
  });
}

// All payments for the current user, newest first.
export function useUserPayments() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['payments', 'list', user?.id],
    queryFn: async (): Promise<Payment[]> => {
      const { data, error } = await supabase
        .from('payments')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []) as Payment[];
    },
    enabled: !!user?.id,
  });
}

// Most recent payment for a given invitation (used by the dashboard banner).
export function useLatestPayment(invitationId: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['payments', 'latest', invitationId],
    queryFn: async (): Promise<Payment | null> => {
      const { data, error } = await supabase
        .from('payments')
        .select('*')
        .eq('invitation_id', invitationId as string)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return (data as Payment) ?? null;
    },
    enabled: !!user?.id && !!invitationId,
  });
}

const RECEIPTS_BUCKET = 'payment-receipts';

export interface CreatePaymentInput {
  invitationId: string;
  amountUsd: number;
  amountBs: number;
  file: File;
  bankReference?: string;
  notes?: string;
}

// Sanitize a filename to a storage-safe slug while keeping the extension.
const safeFileName = (name: string): string => {
  const dot = name.lastIndexOf('.');
  const base = (dot > 0 ? name.slice(0, dot) : name)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'comprobante';
  const ext = dot > 0 ? name.slice(dot + 1).toLowerCase().replace(/[^a-z0-9]/g, '') : '';
  return ext ? `${base}.${ext}` : base;
};

// Uploads the receipt to private storage, then inserts a pending payment row.
export function useCreatePayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreatePaymentInput): Promise<Payment> => {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id;
      if (!userId) throw new Error('No autenticado');

      const eventId = await ensureEventId(userId);
      const path = `${userId}/${Date.now()}-${safeFileName(input.file.name)}`;

      const { error: uploadError } = await supabase.storage
        .from(RECEIPTS_BUCKET)
        .upload(path, input.file, {
          contentType: input.file.type || undefined,
          upsert: false,
        });

      if (uploadError) {
        throw new Error('No se pudo subir el comprobante: ' + uploadError.message);
      }

      const { data, error } = await supabase
        .from('payments')
        .insert({
          user_id: userId,
          event_id: eventId,
          invitation_id: input.invitationId,
          amount_usd: input.amountUsd,
          amount_bs: input.amountBs,
          status: 'pending',
          receipt_url: path,
          bank_reference: input.bankReference?.trim() || null,
          notes: input.notes?.trim() || null,
        })
        .select()
        .single();

      if (error) {
        // Roll back the orphaned upload best-effort so retries stay clean.
        await supabase.storage.from(RECEIPTS_BUCKET).remove([path]);
        throw error;
      }

      return data as Payment;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      toast.success('Comprobante enviado. Tu pago está en revisión.');
    },
    // Errors surface in the modal so it can stay open; no toast here.
  });
}
